/**
 * Remember how a user signs in, so the next visit can skip the email form
 * (EPD-79).
 *
 * SSO users: the hint is the connection name (`Novanta-account-id-7760`),
 * which Auth0 takes as `connection=` and Cognito as `identity_provider=`. It is
 * read from the user's own ID token after sign-in, never from the public
 * /auth/idp lookup, so that endpoint still reveals no connection names. No
 * email is stored for them.
 *
 * Username-password users (Auth0 database or native Cognito) have no
 * connection to skip to, so their hint is their email (product decision:
 * personal data in the cookie for this lane only). /login then does exactly
 * what typing it into the form would.
 *
 * Not HttpOnly: the SPA must read it to start the SDK / PKCE redirect itself.
 */

export type RealmHint =
  | { lane: 'auth0' | 'cognito'; connection: string }
  | { lane: 'password'; email: string };

// __Host-: the browser only accepts it from app.ept.ai itself (Secure, Path=/,
// no Domain), so a sibling *.ept.ai host cannot plant a hint here.
const COOKIE_NAME = '__Host-ept_realm';
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
// One automatic redirect per tab. sessionStorage survives the top-level round
// trip to the IdP, so a second arrival at /login means that attempt failed.
const ATTEMPT_KEY = 'eptai_realm_redirect_attempted';

const CONNECTION_PATTERN = /^[A-Za-z0-9-]+-account-id-\d+$/;
const COOKIE_VALUE_PATTERN = /^(auth0|cognito):([A-Za-z0-9-]+-account-id-\d+)$/;
const PASSWORD_COOKIE_PREFIX = 'password:';
// Stricter than the form: this value starts sign-in with no one typing it, so
// anything unusual (non-ASCII, over the RFC 5321 length) falls back to the form.
const MAX_EMAIL_LENGTH = 254;
const SAFE_EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

function _isSafeEmail(email: string): boolean {
  return email.length <= MAX_EMAIL_LENGTH && SAFE_EMAIL_PATTERN.test(email);
}

function _cognitoProviderName(claims: Record<string, unknown>): string | undefined {
  let identities = claims.identities;
  if (typeof identities === 'string') {
    try {
      identities = JSON.parse(identities);
    } catch {
      identities = undefined;
    }
  }
  if (Array.isArray(identities)) {
    const providerName = (identities[0] as { providerName?: unknown } | undefined)?.providerName;
    if (typeof providerName === 'string') {
      return providerName;
    }
  }
  // Federated usernames are "<ProviderName>_<sub>".
  const username = claims['cognito:username'];
  return typeof username === 'string' ? username.split('_')[0] : undefined;
}

/**
 * How a signed-in user came in: their SSO connection, their email for
 * username-password users, or null for anyone else (social logins), who keep
 * the email form.
 *
 * Examples:
 *   { sub: 'waad|Novanta-account-id-7760|8f2c' } -> { lane: 'auth0', connection: 'Novanta-account-id-7760' }
 *   { iss: 'https://cognito-idp…', identities: [{ providerName: 'Novanta-account-id-7760' }] } -> { lane: 'cognito', … }
 *   { iss: 'https://cognito-idp…', 'cognito:username': 'b1c2', email: 'pat@acme.com' } -> { lane: 'password', … }
 *   { sub: 'auth0|65a1', email: 'Pat@Acme.com' } -> { lane: 'password', email: 'pat@acme.com' }
 *   { sub: 'google-oauth2|1234' } -> null
 */
export function realmFromClaims(
  claims: Record<string, unknown> | null | undefined
): RealmHint | null {
  if (!claims) {
    return null;
  }
  if (typeof claims.iss === 'string' && claims.iss.includes('cognito-idp.')) {
    const connection = _cognitoProviderName(claims);
    if (connection && CONNECTION_PATTERN.test(connection)) {
      return { lane: 'cognito', connection };
    }
    // Federated users carry `identities`; without it this is a native pool user.
    return claims.identities === undefined ? _passwordHint(claims) : null;
  }
  // Auth0 enterprise user ids are "<strategy>|<connection>|<id>".
  const parts = String(claims.sub ?? '').split('|');
  if (parts.length >= 3 && CONNECTION_PATTERN.test(parts[1])) {
    return { lane: 'auth0', connection: parts[1] };
  }
  // Auth0 database (username-password) user ids are "auth0|<id>".
  if (parts.length === 2 && parts[0] === 'auth0') {
    return _passwordHint(claims);
  }
  return null;
}

function _passwordHint(claims: Record<string, unknown>): RealmHint | null {
  if (typeof claims.email !== 'string') {
    return null;
  }
  const email = claims.email.trim().toLowerCase();
  return _isSafeEmail(email) ? { lane: 'password', email } : null;
}

/** The saved hint, or null when absent or not in the expected shape. */
export function readRealmHint(): RealmHint | null {
  try {
    const entry = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(`${COOKIE_NAME}=`));
    const value = entry?.slice(COOKIE_NAME.length + 1) ?? '';
    if (value.startsWith(PASSWORD_COOKIE_PREFIX)) {
      const email = decodeURIComponent(value.slice(PASSWORD_COOKIE_PREFIX.length));
      return _isSafeEmail(email) ? { lane: 'password', email } : null;
    }
    const match = value.match(COOKIE_VALUE_PATTERN);
    return match ? { lane: match[1] as 'auth0' | 'cognito', connection: match[2] } : null;
  } catch {
    return null;
  }
}

/** @internal test helper */
export function buildRealmHintCookieValue(hint: RealmHint | null): string {
  let value = '';
  if (hint?.lane === 'password') {
    value = `${PASSWORD_COOKIE_PREFIX}${encodeURIComponent(hint.email)}`;
  } else if (hint) {
    value = `${hint.lane}:${hint.connection}`;
  }
  const maxAge = hint ? MAX_AGE_SECONDS : 0;
  return `${COOKIE_NAME}=${value}; Path=/; Secure; SameSite=Lax; Max-Age=${maxAge}`;
}

export function writeRealmHint(hint: RealmHint): void {
  try {
    document.cookie = buildRealmHintCookieValue(hint);
  } catch {
    // Must never affect sign-in.
  }
}

export function clearRealmHint(): void {
  try {
    document.cookie = buildRealmHintCookieValue(null);
  } catch {
    // Must never affect sign-in.
  }
}

export function hasTriedRealmRedirect(): boolean {
  try {
    return sessionStorage.getItem(ATTEMPT_KEY) === '1';
  } catch {
    // No storage means no loop guard, so never auto-redirect.
    return true;
  }
}

/**
 * Whether /login should skip the form and redirect to this hint's IdP now.
 * Records the attempt, so it answers true at most once per tab.
 */
export function claimRealmRedirect(): RealmHint | null {
  const hint = readRealmHint();
  if (!hint || hasTriedRealmRedirect() || !markRealmRedirectTried()) {
    return null;
  }
  return hint;
}

/** False when the flag cannot be stored; callers must then not auto-redirect. */
export function markRealmRedirectTried(): boolean {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, '1');
    return sessionStorage.getItem(ATTEMPT_KEY) === '1';
  } catch {
    return false;
  }
}

export function resetRealmRedirect(): void {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // Nothing to reset.
  }
}
